import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { colors } from '../../lib/theme';
import { LENS_LABELS } from '../../lib/lens-labels';
import type { ConvertedScreeningRow, UiVerdict } from '../../services/api';

export function statusMeta(value: UiVerdict) {
  switch (value) {
    case 'clean':
      return { label: 'Clean', color: colors.green, Icon: CheckCircle2 };
    case 'block':
      return { label: 'Excluded', color: colors.red, Icon: XCircle };
    case 'review':
    default:
      return { label: 'Review', color: colors.yellow, Icon: AlertTriangle };
  }
}

export function SummaryPill({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium"
      style={{
        border: `1px solid ${color}4d`,
        backgroundColor: `${color}21`,
        color,
      }}
    >
      <span className="font-mono-num font-bold text-sm">{count}</span>
      <span style={{ color: colors.textSecondary, fontWeight: 500 }}>{label}</span>
    </span>
  );
}

function occupationStatusMeta(value: UiVerdict) {
  switch (value) {
    case 'block':
      return { label: LENS_LABELS.occupationFlagged, color: colors.red, Icon: XCircle };
    case 'review':
      return { label: LENS_LABELS.occupationNeedsReview, color: colors.yellow, Icon: AlertTriangle };
    case 'clean':
    default:
      return { label: LENS_LABELS.occupationNotFlagged, color: colors.textMuted, Icon: CheckCircle2 };
  }
}

export function OccupationStatusPill({ bds }: { bds: ConvertedScreeningRow['bds'] }) {
  const { label, color, Icon } = occupationStatusMeta(bds.overall);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm"
      style={{
        backgroundColor: `${color}15`,
        border: `1px solid ${color}30`,
        color,
      }}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </span>
  );
}

export function StatusPill({
  value,
  labelOverride,
}: {
  value: UiVerdict | ConvertedScreeningRow['bds'];
  labelOverride?: string;
}) {
  const statusValue: UiVerdict =
    typeof value === 'object' && value !== null && 'overall' in value ? value.overall : value;

  const { label, color, Icon } = statusMeta(statusValue);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm"
      style={{
        backgroundColor: `${color}15`,
        border: `1px solid ${color}30`,
        color,
      }}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{labelOverride ?? label}</span>
    </span>
  );
}

export function FinalVerdict({ value }: { value: UiVerdict }) {
  const { label, color, Icon } = statusMeta(value);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm"
      style={{
        backgroundColor: `${color}20`,
        border: `1px solid ${color}40`,
        color,
      }}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </span>
  );
}
